import { Alert } from '@heroui/alert';
import { Button } from '@heroui/button';
import { HiMusicNote } from 'react-icons/hi';
import { LuPlus } from 'react-icons/lu';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Songbook } from '@/core/db.ts';
import { useAddSong, useSongs } from '@/hooks/useSongbook.ts';

import SongsListTable from './SongsListTable.tsx';

interface SongbookListProps {
  songbook: Songbook;
}

function SongsList({ songbook }: SongbookListProps) {
  const { t } = useTranslation();
  const { isLoading, data: songs, error } = useSongs(songbook.id);
  const { mutateAsync: addSong, isPending } = useAddSong(songbook.id);
  const navigate = useNavigate();

  const onHandleAddSong = useCallback(async () => {
    const id = await addSong();

    navigate(`/songbook/${songbook.id}/song/${id}`);
  }, [addSong, navigate, songbook.id]);

  if (isLoading) {
    return null;
  }

  if (error || !songs) {
    return (
      <div className="flex justify-center my-8">
        <Alert
          className="sm:max-w-1/2"
          color="danger"
          description={t('songbook.songList.errorDescription')}
          title={t('songbook.songList.errorTitle')}
          variant="faded"
        />
      </div>
    );
  }

  return (
    <>
      {songs.length === 0 ? (
        <div className="my-8 flex justify-center">
          <Alert
            className="max-w-140"
            description={t('songbook.songList.emptyDescription')}
            endContent={
              <Button disabled={isPending} size="sm" startContent={<LuPlus />} variant="flat" onPress={onHandleAddSong}>
                {t('songbook.songList.emptyButton')}
              </Button>
            }
            icon={<HiMusicNote />}
            title={t('songbook.songList.emptyTitle')}
            variant="faded"
          />
        </div>
      ) : (
        <div className="my-4">
          <Button color="primary" disabled={isPending} startContent={<LuPlus />} variant="solid" onPress={onHandleAddSong}>
            {t('songbook.songList.addSong')}
          </Button>

          <SongsListTable songbook={songbook} songs={songs} />
        </div>
      )}
    </>
  );
}

export default SongsList;
